// Deterministic event IDs (RFC 9562 UUID version 5), so a retried mapping appends the same
// events and the event store rejects the duplicates (CLAUDE.md "Data model").

import { createHash } from 'node:crypto';

/** Namespace for Diagnuvo event IDs. A fixed, random UUID; never change it. */
export const EVENT_ID_NAMESPACE = '6f2c1d8e-4b7a-4c39-9e51-3a0d2b8f7c64';

function uuidBytes(uuid: string): Buffer {
  const hex = uuid.replaceAll('-', '');
  if (!/^[0-9a-f]{32}$/i.test(hex)) throw new Error(`Not a UUID: ${uuid}`);
  return Buffer.from(hex, 'hex');
}

export function uuidV5(name: string, namespace: string): string {
  const hash = createHash('sha1').update(uuidBytes(namespace)).update(name, 'utf8').digest();
  const bytes = hash.subarray(0, 16);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x50; // version 5
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // RFC 9562 variant
  const hex = bytes.toString('hex');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The ID of the `index`-th event (0-based) mapped from one source message by mapper version
 * `mapperVersion`. Version 1 IDs leave the version out, so they never change; a newer version
 * gives new IDs, so a replay after a mapper fix appends instead of being skipped as a duplicate.
 */
export function eventId(
  siteId: string,
  source: string,
  controlId: string,
  index: number,
  mapperVersion = 1,
): string {
  if (!Number.isInteger(index) || index < 0) throw new Error(`eventId: bad index ${index}`);
  if (!Number.isInteger(mapperVersion) || mapperVersion < 1) {
    throw new Error(`eventId: bad mapper version ${mapperVersion}`);
  }
  const version = mapperVersion === 1 ? '' : `|v${mapperVersion}`;
  return uuidV5(`${siteId}|${source}|${controlId}|${index}${version}`, EVENT_ID_NAMESPACE);
}
